import React, { useState } from "react";
import { Button, Space, Card, Typography, Divider, Alert, Steps, Collapse, Tag } from "antd";
import { InfoCircleOutlined, EditOutlined, SendOutlined, CodeOutlined } from "@ant-design/icons";
import GeneralEmailEditor from "../../components/EmailEditor/GeneralEmailEditor";
import { CONTENT_BLOCK_TYPES } from "../../components/EmailEditor/constants/contentBlockConstants";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * 模板驱动邮件编辑器演示页面
 * 包含详细的使用说明和模板编辑指南
 */
const TemplateDrivenDemoPage = () => {
    const [editorVisible, setEditorVisible] = useState(false);

    // 示例模板内容（包含占位符）
    const templateContent = `
        <h2>航食申请邮件</h2>
        <p>Dear Catering Team,</p>
        <p>Please find attached catering requests for the following flight:</p>

        <div data-content-block-type="flightWithPredecessor"
             data-content-block-id="flight_catering_info"
             data-content-block-label="Flight Catering Information">
            Click to insert flight catering details
        </div>

        <p>Please confirm the receipt and send delivery notes to cateringorder@flymeta.com</p>
        <p>Thanks!</p>
    `;

    // 示例业务上下文数据
    const businessContext = {
        contentBlocks: [
            {
                id: "flight_catering_info",
                type: "flightWithPredecessor",
                title: "航班及前序航班信息",
                data: {
                    currentFlight: {
                        flight_number: "CT107",
                        flight_date: "2024-08-18",
                        departure_airport: "HGH",
                        arrival_airport: "ALA",
                        std: "2024-08-18T20:55:00+08:00",
                        sta: "2024-08-19T17:30:00+08:00",
                        aircraft_reg: "9H-FLM",
                        stc_type: "PAX"
                    },
                    predecessorFlight: {
                        flight_number: "CT101",
                        flight_date: "2024-08-18",
                        departure_airport: "LGG",
                        arrival_airport: "HGH",
                        std: "2024-08-18T17:30:00+08:00",
                        sta: "2024-08-18T19:55:00+08:00",
                        aircraft_reg: "9H-FLM",
                        stc_type: "PAX"
                    }
                }
            }
        ]
    };

    const handleSubmit = (emailData) => {
        console.log("邮件数据:", emailData);
        alert("邮件发送成功！（这是演示）");
        setEditorVisible(false);
    };

    const handleCancel = () => {
        setEditorVisible(false);
    };

    // 可用的内容块类型
    const availableContentBlocks = [
        { type: CONTENT_BLOCK_TYPES.FLIGHT_INFO, label: "单个航班信息", description: "插入单个航班的详细信息" },
        { type: CONTENT_BLOCK_TYPES.FLIGHT_LIST, label: "航班列表", description: "插入航班列表表格" },
        { type: CONTENT_BLOCK_TYPES.ATTACHMENT, label: "单个附件", description: "插入单个附件信息" },
        { type: CONTENT_BLOCK_TYPES.ATTACHMENT_LIST, label: "附件列表", description: "插入附件列表" },
        { type: CONTENT_BLOCK_TYPES.CONTACT, label: "单个联系人", description: "插入单个联系人信息" },
        { type: CONTENT_BLOCK_TYPES.CONTACT_LIST, label: "联系人列表", description: "插入联系人列表" },
        { type: CONTENT_BLOCK_TYPES.CARGO_MANIFEST, label: "货物清单", description: "插入货物清单信息" },
        { type: CONTENT_BLOCK_TYPES.CREW_INFO, label: "机组信息", description: "插入机组信息" },
        { type: CONTENT_BLOCK_TYPES.SCHEDULE_PERIOD, label: "计划周期", description: "插入计划周期信息" },
    ];

    return (
        <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
            <Title level={2}>
                <EditOutlined style={{ marginRight: 8 }} />
                模板驱动邮件编辑器
            </Title>

            <Alert message="智能邮件编辑新体验" description="通过模板中的内容块，系统会自动识别并生成对应的侧边栏，让邮件编辑更加智能和高效。" type="info" showIcon style={{ marginBottom: 24 }} />

            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* 功能介绍 */}
                <Card title="✨ 核心功能特点" size="small">
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <Text>
                            • <strong>智能识别</strong>：自动解析模板中的内容块，生成对应的侧边栏
                        </Text>
                        <Text>
                            • <strong>精确替换</strong>：点击侧边栏内容时，精确替换模板中的对应位置
                        </Text>
                        <Text>
                            • <strong>动态构建</strong>：根据业务数据动态构建侧边栏内容
                        </Text>
                        <Text>
                            • <strong>类型丰富</strong>：支持航班、附件、联系人等多种内容类型
                        </Text>
                    </Space>
                </Card>

                {/* 使用步骤 */}
                <Card title="📋 使用步骤" size="small">
                    <Steps
                        direction="vertical"
                        size="small"
                        items={[
                            {
                                title: "编写邮件模板",
                                description: "在模板中插入内容块标签，系统会自动识别",
                                icon: <CodeOutlined />,
                            },
                            {
                                title: "准备业务数据",
                                description: "提供完整的业务上下文数据（航班、附件、联系人等）",
                                icon: <InfoCircleOutlined />,
                            },
                            {
                                title: "打开编辑器",
                                description: "系统自动解析模板，生成对应的侧边栏内容",
                                icon: <EditOutlined />,
                            },
                            {
                                title: "编辑并发送",
                                description: "点击侧边栏内容插入到邮件中，完成编辑后发送",
                                icon: <SendOutlined />,
                            },
                        ]}
                    />
                </Card>

                {/* 模板编辑指南 */}
                <Card title="📝 模板编辑指南" size="small">
                    <Collapse>
                        <Panel header="1. 内容块语法格式" key="syntax">
                            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                <Alert
                                    message="标准格式"
                                    description={
                                        <div>
                                            <Paragraph code>{`<div data-content-block-type="内容块类型" data-content-block-label="提示文字"></div>`}</Paragraph>
                                            <Text type="secondary">
                                                • data-content-block-type: 指定内容块类型（必需）
                                                <br />• data-content-block-label: 用户看到的提示文字（可选）
                                            </Text>
                                        </div>
                                    }
                                    type="info"
                                />

                                <div>
                                    <Text strong>示例：</Text>
                                    <Paragraph code>{`<div data-content-block-type="flightList" data-content-block-label="点击右侧插入航班列表"></div>`}</Paragraph>
                                </div>
                            </Space>
                        </Panel>

                        <Panel header="2. 可用的内容块类型" key="types">
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                {availableContentBlocks.map((block) => (
                                    <div key={block.type} style={{ padding: "8px", border: "1px solid #f0f0f0", borderRadius: "4px" }}>
                                        <Space>
                                            <Tag color="blue">{block.type}</Tag>
                                            <Text strong>{block.label}</Text>
                                            <Text type="secondary">- {block.description}</Text>
                                        </Space>
                                    </div>
                                ))}
                            </Space>
                        </Panel>

                        <Panel header="3. 模板编写最佳实践" key="best-practices">
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                <Text>
                                    • <strong>语义化命名</strong>：使用清晰的 data-content-block-label 描述内容块用途
                                </Text>
                                <Text>
                                    • <strong>合理布局</strong>：将内容块放在合适的位置，保持邮件结构清晰
                                </Text>
                                <Text>
                                    • <strong>类型匹配</strong>：确保内容块类型与实际业务数据匹配
                                </Text>
                                <Text>
                                    • <strong>预留空间</strong>：为动态内容预留足够的显示空间
                                </Text>
                                <Text>
                                    • <strong>测试验证</strong>：编写完模板后及时测试效果
                                </Text>
                            </Space>
                        </Panel>

                        <Panel header="4. 常见问题解决" key="troubleshooting">
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                <Text>
                                    • <strong>内容块不显示</strong>：检查 data-content-block-type 是否正确
                                </Text>
                                <Text>
                                    • <strong>侧边栏为空</strong>：确认业务数据中包含对应的数据类型
                                </Text>
                                <Text>
                                    • <strong>替换不生效</strong>：检查内容块标签格式是否完整
                                </Text>
                                <Text>
                                    • <strong>样式异常</strong>：避免在内容块标签上添加额外的样式
                                </Text>
                            </Space>
                        </Panel>
                    </Collapse>
                </Card>

                {/* 当前示例 */}
                <Card title="🎯 当前演示示例" size="small">
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                        <div>
                            <Text strong>模板内容：</Text>
                            <div
                                style={{
                                    border: "1px solid #d9d9d9",
                                    padding: "12px",
                                    borderRadius: "6px",
                                    backgroundColor: "#fafafa",
                                    fontSize: "12px",
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                    maxHeight: "200px",
                                    overflow: "auto",
                                }}
                            >
                                {templateContent}
                            </div>
                        </div>

                        <div>
                            <Text strong>业务数据：</Text>
                            <Space>
                                <Tag color="green">航班数据：{businessContext.flights.length} 条</Tag>
                                <Tag color="orange">附件数据：{businessContext.attachments.length} 个</Tag>
                                <Tag color="purple">联系人数据：{businessContext.contacts.length} 个</Tag>
                            </Space>
                        </div>

                        <Divider />

                        <Button type="primary" size="large" icon={<EditOutlined />} onClick={() => setEditorVisible(true)}>
                            打开模板驱动邮件编辑器
                        </Button>
                    </Space>
                </Card>
            </Space>

            <GeneralEmailEditor visible={editorVisible} title="模板驱动邮件编辑器演示" templateContent={templateContent} businessContext={businessContext} initialSubject="航班计划通知 - 2024-12-20" to="recipient@airline.com" onSubmit={handleSubmit} onCancel={handleCancel} />
        </div>
    );
};

export default TemplateDrivenDemoPage;
